import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Inbox, Loader2, Mail, Building2, Calendar } from "lucide-react";
import { motion } from "framer-motion";

const STATUS = {
  new: { label: "Nouveau", c: "bg-blue-100 text-blue-700" },
  contacted: { label: "Contacté", c: "bg-amber-100 text-amber-700" },
  done: { label: "Traité", c: "bg-emerald-100 text-emerald-700" },
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); } catch { return "—"; } };

export default function Prospects() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("leads");
  const [trials, setTrials] = useState(null);

  const load = () => api.get("/admin/leads").then((r) => setData(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  const loadTrials = () => api.get("/admin/trials").then((r) => setTrials(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); loadTrials(); /* eslint-disable-next-line */ }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/leads/${id}`, { status }); load(); toast.success("Statut mis à jour."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data) return <div className="flex h-64 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const leads = filter === "all" ? data.leads : data.leads.filter((l) => l.status === filter);

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 lg:px-12" data-testid="prospects-page">
      <header className="mb-6">
        <p className="overline text-[#C6A87C]">Espace administrateur</p>
        <h1 className="mt-2 font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">Prospects</h1>
        <p className="mt-2 text-slate-500">Demandes de contact et essais gratuits 60 min.</p>
      </header>

      <div className="mb-6 flex gap-2">
        <button onClick={() => setTab("leads")} data-testid="tab-leads" className={`rounded-full px-4 py-2 text-sm font-medium ${tab === "leads" ? "bg-[#0F172A] text-white" : "bg-white text-slate-600 border border-slate-200"}`}>Contacts ({data.total})</button>
        <button onClick={() => setTab("trials")} data-testid="tab-trials" className={`rounded-full px-4 py-2 text-sm font-medium ${tab === "trials" ? "bg-[#0F172A] text-white" : "bg-white text-slate-600 border border-slate-200"}`}>Essais gratuits ({trials?.stats?.total ?? 0})</button>
      </div>

      {tab === "trials" ? (
        <div data-testid="trials-panel">
          {!trials ? (
            <div className="flex h-40 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : trials.trials.length === 0 ? (
            <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-200 py-16 text-slate-400"><Inbox className="h-8 w-8" /><p className="mt-3 text-sm">Aucun essai pour le moment.</p></div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-slate-200 bg-white p-3"><p className="text-xs text-slate-400">Total</p><p className="mt-1 font-display text-2xl text-[#0F172A]">{trials.stats.total}</p></div>
                <div className="rounded-xl border border-slate-200 bg-white p-3"><p className="text-xs text-slate-400">Activés</p><p className="mt-1 font-display text-2xl text-emerald-600">{trials.stats.converted}</p></div>
                <div className="rounded-xl border border-slate-200 bg-white p-3"><p className="text-xs text-slate-400">En attente</p><p className="mt-1 font-display text-2xl text-amber-600">{trials.stats.pending}</p></div>
              </div>
              {trials.trials.map((t, i) => (
                <motion.div key={t.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                  data-testid={`trial-${t.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-[#0F172A]">{t.name} <span className="ml-1 text-xs text-slate-400">{t.country}</span></p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                        <a href={`mailto:${t.email}`} className="inline-flex items-center gap-1.5 hover:text-[#0F172A]"><Mail className="h-3.5 w-3.5" /> {t.email}</a>
                        <span>{t.phone}</span>
                        <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {fmt(t.created_at)}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                        <span className={`rounded-full px-2 py-0.5 ${t.email_verified ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>E-mail {t.email_verified ? "vérifié" : "non vérifié"}</span>
                        <span className={`rounded-full px-2 py-0.5 ${t.phone_verified ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>Tél {t.phone_verified ? "vérifié" : (t.sms_sent ? "SMS envoyé" : "non vérifié")}</span>
                        {t.referrer && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">src: {t.referrer}</span>}
                        {t.utm_source && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">utm: {t.utm_source}</span>}
                        {t.device && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{t.device}</span>}
                        {t.device_fp && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-slate-600" data-testid={`trial-devicefp-${t.id}`}>appareil: {String(t.device_fp).slice(0, 14)}</span>}
                        {t.language && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{t.language}</span>}
                      </div>
                    </div>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${t.converted ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{t.converted ? "Essai activé" : "En attente"}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      ) : (
      <>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <button onClick={() => setFilter("all")} data-testid="filter-all" className={`rounded-xl border p-3 text-left ${filter === "all" ? "border-[#C6A87C] bg-[#C6A87C]/10" : "border-slate-200 bg-white"}`}>
          <p className="text-xs text-slate-400">Total</p><p className="mt-1 font-display text-2xl text-[#0F172A]">{data.total}</p>
        </button>
        {["new", "contacted", "done"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`} className={`rounded-xl border p-3 text-left ${filter === s ? "border-[#C6A87C] bg-[#C6A87C]/10" : "border-slate-200 bg-white"}`}>
            <p className="text-xs text-slate-400">{STATUS[s].label}</p><p className="mt-1 font-display text-2xl text-[#0F172A]">{data.counts[s] || 0}</p>
          </button>
        ))}
      </div>

      {leads.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-200 py-16 text-slate-400" data-testid="prospects-empty">
          <Inbox className="h-8 w-8" />
          <p className="mt-3 text-sm">Aucun prospect pour le moment.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {leads.map((l, i) => (
            <motion.div key={l.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              data-testid={`prospect-${l.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-[#0F172A]">{l.name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-[#0F172A]"><Mail className="h-3.5 w-3.5" /> {l.email}</a>
                    {l.company && <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> {l.company}</span>}
                    <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {fmt(l.created_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS[l.status]?.c || STATUS.new.c}`}>{STATUS[l.status]?.label || "Nouveau"}</span>
                  <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                    <SelectTrigger className="h-9 w-36" data-testid={`prospect-status-${l.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Nouveau</SelectItem>
                      <SelectItem value="contacted">Contacté</SelectItem>
                      <SelectItem value="done">Traité</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="mt-3 whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-sm text-slate-600">{l.message}</p>
            </motion.div>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}
